/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, lazy, Suspense, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { FlagGroup } from '@atlaskit/flag';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import PenIcon from '@atlaskit/icon/core/pen';
import InfoIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import ProfileClient from '@atlaskit/profilecard/client';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { AiBrand } from '../../../constants';
import { onCuratedDefinitionSeeEditHistoryButtonClicked } from '../analytics';
import { ATLASSIAN_INTELLIGENCE_TRUST_LINK, ATLASSIAN_ROVO_LINK } from '../common/constants';
import { AtlassianIntelligenceIcon } from '../common/icons/atlassian-intelligence-icon';
import { DefinitionUpdateConfirmationFlag } from '../curated/edit-definition-modal/definition-update-confirmation-flag';
import { EditDefinitionModal } from '../curated/edit-definition-modal/edit-definition-modal';

import { FeedbackButtons } from './feedback-buttons';
import { FeedbackModal } from './feedback-modal';
import { FeedbackOptionsModal } from './feedback-options-modal';
import { messages } from './messages';

const styles = cssMap({
	aiFooter: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		width: '100%',
	},
	aiFooterUpdates: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	rovoWrapper: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
	contentMayVaryWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	brandWrapper: {
		display: 'flex',
		alignItems: 'center',
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	editedByFooter: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	editedByWrapper: {
		display: 'flex',
		backgroundColor: token('color.background.neutral'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		borderRadius: token('radius.full'),
		marginLeft: token('space.050'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	definedByWrapper: {
		marginLeft: token('space.100'),
	},
	activeEditedByWrapper: {
		display: 'flex',
		backgroundColor: token('color.background.neutral.hovered'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		borderRadius: token('radius.full'),
		marginLeft: token('space.050'),
		cursor: 'pointer',
	},
	editHistoryWrapper: {
		display: 'flex',
		font: token('font.body.small'),
		color: token('color.link'),
		paddingLeft: token('space.300'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

const AkFeedbackFlag = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

export const AIDefinitionFooterSection: () => JSX.Element = () => {
	const { formatMessage, locale } = useIntl();
	const { answerEditor, brand, readingAidsOptions } = useAIAnswerContext();

	const { definitionCurationOptions } = readingAidsOptions || {};
	const editDefinitionEnabled = !!definitionCurationOptions;

	const [showFeedbackOptionsModal, setShowFeedbackOptionsModal] = useState(false);
	const [showEditDefinitionModal, setShowEditDefinitionModal] = useState(false);

	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const [displayFlag, setDisplayFlag] = useState(false);
	const [displaySuccessFlag, setDisplaySuccessFlag] = useState(false);

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	return (
		<Fragment>
			<Box xcss={cx(styles.aiFooter, serveDefinitionUXUpdates && styles.aiFooterUpdates)}>
				<Box xcss={styles.contentMayVaryWrapper}>
					{brand === AiBrand.ROVO ? null : (
						<Lozenge>{formatMessage(messages.definition_footer_beta_status_label)}</Lozenge>
					)}
					<InfoIcon label="" color={token('color.icon.subtle')} size="small" />
					<Link
						rel="noopener noreferrer"
						appearance="subtle"
						href={ATLASSIAN_INTELLIGENCE_TRUST_LINK}
					>
						{formatMessage(messages.definition_footer_disclaimer)}
					</Link>
				</Box>
				{!serveDefinitionUXUpdates && (
					<Box xcss={styles.brandWrapper}>
						{brand === AiBrand.ROVO ? (
							<Flex>
								<Flex alignItems="center" gap="space.025">
									<Link rel="noopener noreferrer" appearance="subtle" href={ATLASSIAN_ROVO_LINK}>
										Rovo
									</Link>
									<RovoIcon label="" color={token('color.icon.subtle')} spacing="spacious" />
								</Flex>
							</Flex>
						) : (
							<Flex alignItems="center" gap="space.025">
								{formatMessage(messages.definition_footer_powered_by_AI)}
								<AtlassianIntelligenceIcon greyed size="large" />
							</Flex>
						)}
					</Box>
				)}
				{serveDefinitionUXUpdates && (
					<Box>
						<FeedbackButtons
							setShowFeedbackOptionsModal={setShowFeedbackOptionsModal}
							setDisplayFlag={setDisplayFlag}
						/>
					</Box>
				)}
			</Box>
			<ModalTransition>
				{showFeedbackOptionsModal && (
					<FeedbackOptionsModal
						setShowFeedbackOptionsModal={setShowFeedbackOptionsModal}
						setShowEditDefinitionModal={setShowEditDefinitionModal}
						setShowFeedbackModal={setShowFeedbackModal}
						editDefinitionEnabled={editDefinitionEnabled}
					/>
				)}
				{showFeedbackModal && (
					<FeedbackModal
						setFeedbackModal={setShowFeedbackModal}
						isCuratedDefinition={!!answerEditor}
						setDisplayFlag={setDisplayFlag}
					/>
				)}
				{showEditDefinitionModal && editDefinitionEnabled && (
					<EditDefinitionModal
						curatedDefinitionScopeConfig={definitionCurationOptions.curatedDefinitionScopeConfig}
						closeEditModal={() => setShowEditDefinitionModal(false)}
						openFeedbackOptionsModal={() => setShowFeedbackOptionsModal(true)}
						onSaveEditModal={() => setDisplaySuccessFlag(true)}
					/>
				)}
			</ModalTransition>
			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<Suspense fallback={<Fragment />}>
						<AkFeedbackFlag
							locale={locale}
							description={formatMessage(messages.definition_footer_feedback_flag_description)}
						/>
					</Suspense>
				</FlagGroup>
			)}
			{displaySuccessFlag && (
				<DefinitionUpdateConfirmationFlag onDismissed={() => setDisplaySuccessFlag(false)} />
			)}
		</Fragment>
	);
};

type EditedDefinitionFooterSectionProps = {
	setShowEditHistoryModal: (showModal: boolean) => void;
};

const DEFAULT_GRAPHQL_DIRECTORY_ENDPOINT = '/gateway/api/directory/graphql';

const profileClient = new ProfileClient({
	url: DEFAULT_GRAPHQL_DIRECTORY_ENDPOINT,
});

export const EditedDefinitionFooterSection: ({
	setShowEditHistoryModal,
}: EditedDefinitionFooterSectionProps) => JSX.Element | null = ({
	setShowEditHistoryModal,
}: EditedDefinitionFooterSectionProps) => {
	const { formatMessage } = useIntl();
	const [editorActive, setEditorActive] = useState<boolean>(false);
	const { answerEditor, commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();
	const { cloudId } = commonAttributes;

	if (!answerEditor) {
		return null; // This line should not be reachable since the component is only rendered when answerEditor is defined
	}

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	return (
		<Box xcss={styles.editedByFooter}>
			<Flex gap="space.100" alignItems="start" direction="column">
				<Flex alignItems="center">
					<PenIcon label="" color={token('color.icon.subtle')} />
					<Box xcss={styles.definedByWrapper}>
						<Text>{formatMessage(messages.definition_footer_defined_by_text)}</Text>
					</Box>
					<ProfileCardTrigger
						userId={answerEditor.id}
						cloudId={cloudId}
						resourceClient={profileClient}
						trigger="click"
						onVisibilityChange={setEditorActive}
					>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Box xcss={editorActive ? styles.activeEditedByWrapper : styles.editedByWrapper}>
							@{answerEditor.name}
						</Box>
					</ProfileCardTrigger>
				</Flex>
				{!serveDefinitionUXUpdates && (
					// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
					<Box
						xcss={styles.editHistoryWrapper}
						onClick={() => {
							fireAnalyticsEvent(onCuratedDefinitionSeeEditHistoryButtonClicked(commonAttributes));
							setShowEditHistoryModal(true);
						}}
					>
						{formatMessage(messages.definition_footer_see_edit_history)}
					</Box>
				)}
			</Flex>
		</Box>
	);
};
